import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronLeft, X, GripHorizontal } from 'lucide-react';
import { 
  getPersianDateDetails, 
  getGregorianDate, 
  PERSIAN_MONTHS, 
  PERSIAN_WEEK_DAYS, 
  persianNumber, 
  formatPersianDate 
} from '../services/utils';
import { Note } from '../types';

interface PersianCalendarProps {
  notes: Note[];
  onAddNote: (date: string, content: string) => void;
  onDeleteNote: (id: string) => void;
}

const PersianCalendar: React.FC<PersianCalendarProps> = ({ notes, onAddNote, onDeleteNote }) => {
  const [currentViewDate, setCurrentViewDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [isDesktop, setIsDesktop] = useState(false);

  // Draggable Modal State
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  
  // Persian details of the currently viewed MONTH
  const pDetails = getPersianDateDetails(currentViewDate);
  const currentJYear = pDetails.jy;
  const currentJMonth = pDetails.jm;

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    // Initial check
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const daysInMonth = (month: number, year: number) => {
    if (month <= 6) return 31;
    if (month <= 11) return 30;
    const isLeap = (((((year - 474) % 2820) + 474) + 38) * 682) % 2816 < 682;
    return isLeap ? 30 : 29;
  };

  const generateCalendarGrid = () => {
    const totalDays = daysInMonth(currentJMonth, currentJYear);
    const startOfMonthGregorian = getGregorianDate(currentJYear, currentJMonth, 1);
    const startDayOfWeek = (startOfMonthGregorian.getDay() + 1) % 7;

    const days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= totalDays; i++) {
      days.push(i);
    }
    return days;
  };

  const handlePrevMonth = () => {
    let newMonth = currentJMonth - 1;
    let newYear = currentJYear;
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    setCurrentViewDate(getGregorianDate(newYear, newMonth, 1));
  };

  const handleNextMonth = () => {
    let newMonth = currentJMonth + 1;
    let newYear = currentJYear;
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    setCurrentViewDate(getGregorianDate(newYear, newMonth, 1));
  };

  const handleDayClick = (day: number) => {
    const gDate = getGregorianDate(currentJYear, currentJMonth, day);
    setSelectedDate(gDate);
    setNoteContent('');
    setShowNoteModal(true);
    // Reset position to center-ish relative to container on open if needed
    setModalPosition({ x: 0, y: 0 }); 
  };

  const saveNote = () => {
    if (selectedDate && noteContent.trim()) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      onAddNote(dateStr, noteContent);
      // Don't close immediately on desktop to allow adding multiple, but maybe close on mobile?
      // User likely wants to add one and done on mobile, or see list.
      // Let's keep it open to see the new note added.
      setNoteContent('');
    }
  };

  // Drag Logic for Modal
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isDesktop) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      setModalPosition({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const grid = generateCalendarGrid();
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 p-4 md:p-6 h-auto md:h-full flex flex-col font-persian relative md:overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
           <h2 className="text-xl md:text-2xl font-bold text-slate-800 dark:text-white">
             {PERSIAN_MONTHS[currentJMonth - 1]} <span className="text-primary-600 dark:text-primary-400">{persianNumber(currentJYear)}</span>
           </h2>
        </div>
        <div className="flex gap-1">
          <button onClick={handlePrevMonth} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors" title="ماه قبل">
            <ChevronRight className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </button>
          <button onClick={handleNextMonth} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors" title="ماه بعد">
            <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </button>
        </div>
      </div>

      {/* Week Days Header */}
      <div className="grid grid-cols-7 mb-4">
        {PERSIAN_WEEK_DAYS.map((day) => (
          <div key={day} className="text-center text-xs md:text-sm font-medium text-slate-400 dark:text-slate-500 py-2">
            {day.split(' ')[0]}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-1 md:gap-2 flex-1 auto-rows-fr">
        {grid.map((day, index) => {
          if (!day) return <div key={`empty-${index}`} className="p-2" />;

          const gDate = getGregorianDate(currentJYear, currentJMonth, day);
          const dateKey = gDate.toISOString().split('T')[0];
          const isToday = dateKey === todayStr;
          const dayNotes = notes.filter(n => n.date === dateKey);
          const hasNotes = dayNotes.length > 0;

          return (
            <div 
              key={day}
              onClick={() => handleDayClick(day)}
              className={`
                relative group rounded-xl p-1 md:p-2 cursor-pointer transition-all duration-200 border
                flex flex-col items-center justify-start min-h-[45px] md:min-h-[80px]
                ${isToday 
                  ? 'bg-primary-50 dark:bg-slate-800 border-primary-500 dark:border-primary-400 shadow-sm' 
                  : 'bg-white dark:bg-slate-900 border-slate-50 dark:border-slate-800 hover:border-primary-100 dark:hover:border-primary-900 hover:shadow-md'}
              `}
            >
              <span className={`text-base md:text-lg font-bold ${isToday ? 'text-primary-700 dark:text-primary-400' : 'text-slate-700 dark:text-slate-300'}`}>
                {persianNumber(day)}
              </span>
              
              {hasNotes && (
                <div className="mt-1 md:mt-2 flex gap-0.5 md:gap-1 flex-wrap justify-center">
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-rose-500 animate-pulse"></div>
                  {dayNotes.length > 1 && <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-rose-300 hidden md:block"></div>}
                </div>
              )}

              {/* Hover Tooltip for Notes (Desktop Only) */}
              {hasNotes && !showNoteModal && (
                <div className="hidden md:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-slate-800 dark:bg-slate-700 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-xl border border-slate-700 dark:border-slate-600">
                  <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-800 dark:bg-slate-700 rotate-45"></div>
                  <ul className="space-y-1 font-sans">
                    {dayNotes.map(note => (
                      <li key={note.id} className="border-b border-slate-700 dark:border-slate-600 last:border-0 pb-1 last:pb-0 text-right truncate">
                        • {note.content}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Note Modal */}
      {showNoteModal && selectedDate && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm md:p-4">
          <div 
            className={`
                bg-white dark:bg-slate-900 
                w-full md:w-full md:max-w-sm 
                h-[100dvh] md:h-auto 
                md:rounded-2xl 
                shadow-2xl overflow-hidden 
                border-slate-100 dark:border-slate-800 
                flex flex-col 
                animate-in slide-in-from-bottom duration-300 md:zoom-in-95
            `}
            style={isDesktop ? { 
              transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
              transition: isDragging ? 'none' : 'transform 0.2s',
              position: 'relative'
            } : {}}
          >
             {/* Header */}
             <div 
               onMouseDown={(e) => isDesktop && handleMouseDown(e)}
               className={`
                   bg-slate-50 dark:bg-slate-800 p-4 md:p-3 flex items-center justify-between 
                   border-b border-slate-100 dark:border-slate-700 shrink-0
                   ${isDesktop ? 'cursor-move select-none' : ''}
               `}
             >
                <div className="flex items-center gap-2 text-slate-500">
                  {isDesktop && <GripHorizontal size={18} />}
                  <span className="text-base md:text-sm font-bold text-slate-700 dark:text-slate-200">
                    {formatPersianDate(selectedDate)}
                  </span>
                </div>
                <button 
                  onClick={() => setShowNoteModal(false)}
                  className="p-2 md:p-0 text-slate-400 hover:text-red-500 transition-colors bg-slate-200/50 md:bg-transparent rounded-full md:rounded-none"
                >
                  <X size={20} className="md:w-[18px] md:h-[18px]" />
                </button>
             </div>

             <div className="p-4 flex-1 flex flex-col overflow-hidden">
               {/* Notes List */}
               <div className="flex-1 overflow-y-auto space-y-3 md:space-y-2 custom-scrollbar mb-4">
                  {notes.filter(n => n.date === selectedDate.toISOString().split('T')[0]).length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400 opacity-60">
                         <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-3">
                            <span className="text-2xl">📝</span>
                         </div>
                         <p className="text-sm">یادداشتی وجود ندارد</p>
                    </div>
                  )}
                  {notes.filter(n => n.date === selectedDate.toISOString().split('T')[0]).map(note => (
                    <div key={note.id} className="bg-slate-50 dark:bg-slate-800/50 p-3 md:p-2 rounded-xl md:rounded-lg flex justify-between items-start group border border-slate-100 dark:border-slate-700/50">
                       <p className="text-base md:text-sm text-slate-700 dark:text-slate-300 break-words flex-1 ml-2 leading-relaxed">{note.content}</p>
                       <button 
                         onClick={() => onDeleteNote(note.id)}
                         className="text-slate-400 hover:text-red-500 p-1 md:p-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                       >
                         <X size={16} />
                       </button>
                    </div>
                  ))}
               </div>

               {/* Add Note Input */}
               <div className="flex gap-3 md:gap-2 shrink-0 pt-2 border-t border-slate-100 dark:border-slate-800 md:border-none md:pt-0">
                 <input 
                   type="text" 
                   value={noteContent}
                   onChange={(e) => setNoteContent(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && saveNote()}
                   placeholder="یادداشت جدید..."
                   className="flex-1 bg-slate-100 dark:bg-slate-800 border-none rounded-xl px-4 py-3 md:px-3 md:py-2 text-base md:text-sm focus:ring-2 focus:ring-primary-500 outline-none text-slate-800 dark:text-white"
                   autoFocus={isDesktop}
                 />
                 <button 
                   onClick={saveNote}
                   disabled={!noteContent.trim()}
                   className="bg-primary-600 text-white px-6 md:px-4 py-3 md:py-2 rounded-xl text-base md:text-sm font-bold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-primary-200 dark:shadow-none"
                 >
                   ثبت
                 </button>
               </div>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersianCalendar;